<section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
  <div class="mb-3 border-b border-slate-100 pb-3">
    <h2 class="!m-0 !text-base !font-semibold !leading-6 text-slate-950">
      {{ 'crm_tasks.timeline.title' | translate }}
    </h2>
  </div>

  <div class="flex flex-col gap-2">
    <textarea
      rows="3"
      class="min-h-20 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-6 text-slate-800 outline-none placeholder:text-slate-400 focus:border-orange-400"
      [formControl]="commentControl"
      [placeholder]="'crm_tasks.timeline.comment_placeholder' | translate"
      [attr.aria-label]="'crm_tasks.timeline.comment_placeholder' | translate"
      (keydown.control.enter)="send()"
      (keydown.meta.enter)="send()"
    ></textarea>
    <div class="flex items-center justify-between gap-2">
      <span class="text-xs text-slate-400">{{ 'crm_tasks.timeline.send_hint' | translate }}</span>
      <app-button [disabled]="!canSend" (onChangeClick)="send()">
        {{ (sending ? 'saving' : 'crm_tasks.timeline.send') | translate }}
      </app-button>
    </div>
  </div>

  <div class="mt-4 flex items-center gap-4 border-b border-slate-200">
    @for (item of filters; track item.key) {
      <button
        type="button"
        class="border-b-2 px-1 pb-2 text-sm font-semibold transition"
        [ngClass]="
          filter === item.key
            ? 'border-orange-500 text-slate-950'
            : 'border-transparent text-slate-400 hover:text-slate-600'
        "
        (click)="filter = item.key"
      >
        {{ item.labelKey | translate }}
      </button>
    }
  </div>

  <div class="mt-3 flex flex-col gap-3">
    @if (loading && !events.length) {
      <div class="h-24 animate-pulse rounded-lg bg-slate-100"></div>
    } @else {
      @for (event of visibleEvents; track event.id) {
        <div class="group flex gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
          <div
            class="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-500"
          >
            <app-icon
              class="inline-flex !h-[18px] !w-[18px] items-center justify-center !text-[18px] !leading-[18px]"
              >{{ event.event_type === 'COMMENT' ? 'comment' : getIcon(event) }}</app-icon
            >
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div class="min-w-0">
                <div class="truncate text-sm font-semibold text-slate-800">
                  {{ (event.event_type === 'COMMENT' ? 'comment' : getTitleKey(event)) | translate }}
                </div>
                <div class="mt-0.5 flex min-w-0 items-center gap-1.5">
                  <app-avatar
                    size="16px"
                    [title]="getPersonName(event.created_by_user)"
                    [url]="event.created_by_user?.avatar_url ?? undefined"
                    [isPhotoGallery]="false"
                  />
                  <span class="truncate text-xs font-medium text-slate-500">{{
                    getPersonName(event.created_by_user)
                  }}</span>
                </div>
              </div>

              <div class="flex shrink-0 items-center gap-1">
                <span class="text-xs text-slate-400">{{ event.created_at | date: 'dd MMM HH:mm' }}</span>
                @if (event.event_type === 'COMMENT' && editingId !== event.id) {
                  <span
                    class="flex items-center opacity-100 transition md:opacity-0 md:focus-within:opacity-100 md:group-hover:opacity-100"
                  >
                    <button
                      type="button"
                      class="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      [attr.aria-label]="'edit' | translate"
                      (click)="startEdit(event)"
                    >
                      <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">edit</app-icon>
                    </button>
                    <button
                      type="button"
                      class="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      [attr.aria-label]="'delete' | translate"
                      (click)="remove(event)"
                    >
                      <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">delete</app-icon>
                    </button>
                  </span>
                }
              </div>
            </div>

            @if (event.event_type === 'COMMENT') {
              @if (editingId === event.id) {
                <div class="mt-2 flex flex-col gap-2">
                  <textarea
                    rows="3"
                    class="min-h-20 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-6 text-slate-800 outline-none focus:border-orange-400"
                    [formControl]="editControl"
                    [attr.aria-label]="'comment' | translate"
                    (keydown.control.enter)="saveEdit(event)"
                    (keydown.meta.enter)="saveEdit(event)"
                    (keydown.escape)="$event.stopPropagation(); cancelEdit()"
                  ></textarea>
                  <div class="flex justify-end gap-2">
                    <app-button btnType="stroked" [disabled]="saving" (onChangeClick)="cancelEdit()">
                      {{ 'cancel' | translate }}
                    </app-button>
                    <app-button [disabled]="!editControl.value.trim() || saving" (onChangeClick)="saveEdit(event)">
                      {{ 'save' | translate }}
                    </app-button>
                  </div>
                </div>
              } @else {
                <div class="mt-2 whitespace-pre-wrap break-words text-sm text-slate-600">{{ getComment(event) }}</div>
              }
            } @else if (event.x_data?.status; as status) {
              <div class="mt-2 flex flex-wrap items-center gap-1.5">
                <app-task-status-badge [status]="status.old" />
                <app-icon class="!h-4 !w-4 !text-base !leading-none text-slate-400">arrow_right_alt</app-icon>
                <app-task-status-badge [status]="status.new" />
              </div>
            } @else if (event.x_data?.deadline; as deadline) {
              <div class="mt-2 flex flex-wrap items-center gap-1.5 text-sm font-medium text-slate-700">
                <span [class.text-slate-400]="!deadline.old">
                  {{
                    deadline.old
                      ? (deadline.old | date: 'dd.MM.yyyy HH:mm')
                      : ('crm_tasks.timeline.no_deadline' | translate)
                  }}
                </span>
                <app-icon class="!h-4 !w-4 !text-base !leading-none text-slate-400">arrow_right_alt</app-icon>
                <span [class.text-slate-400]="!deadline.new">
                  {{
                    deadline.new
                      ? (deadline.new | date: 'dd.MM.yyyy HH:mm')
                      : ('crm_tasks.timeline.no_deadline' | translate)
                  }}
                </span>
              </div>
            }
          </div>
        </div>
      } @empty {
        <div class="flex min-h-24 items-center justify-center rounded-lg bg-slate-50 text-sm text-slate-400">
          {{ 'no_timelines' | translate }}
        </div>
      }
    }
  </div>
</section>
